import { useEffect } from "react";

import { mapApiPaymentToUiRow } from "./paymentLinesErpAdapter";
import { mapApiChargesToOtherAmountEntries } from "./otherAmountCalculations";

const cellValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

export default function useFillSalesFromCustomOrder({
  locationState,
  setTableData,
  setPaymentRows,
  setRoundOffAmount,
  setNote,
  COLUMNS,
  dropdownData,
  hasFilledFromCustomOrder,
  setHasFilledFromCustomOrder,
  userss, // <-- add userss
  setCustomerName,
  setCustomerNameSearchText,
  setCustomerMobile,
  setCustomerMobileSearchText,
  setSelectedCustomerId,
  setCustomerDetails, // <-- add this setter
  setCustomerCity,
  setCustomerAddress,
  setCustomerArea,
  setSelectedState,
  states,
  cities,
  setExpDate, // <-- add this setter
  expDate, // <-- add this value
  isEditMode = false, // <-- add edit mode flag
}) {
  useEffect(() => {
    if (
      !isEditMode && // Don't run in edit mode
      !hasFilledFromCustomOrder &&
      locationState &&
      locationState.customOrderTable &&
      Array.isArray(locationState.customOrderTable) &&
      locationState.customOrderTable.length > 0
    ) {
      const customOrderTable = locationState.customOrderTable;
      const productGroups = Array.isArray(dropdownData?.productGroup)
        ? dropdownData.productGroup
        : [];

      const mappedTableData = customOrderTable.map((row, idx) => ({
        id: idx + 1,
        data: COLUMNS.map((col) => {
          let value = "";
          let extra = {};
          switch (col.id) {
            case "productGroup": {
              const pgId =
                row.product_group ?? row.product_group_id ?? row.product_type;
              const found = productGroups.find(
                (pg) => String(pg.value) === String(pgId),
              );
              value = found
                ? found.label || found.name || ""
                : cellValue(row.product_group_name || pgId);
              break;
            }
            case "design":
              value = cellValue(row.design);
              break;
            case "size":
              value = cellValue(row.size);
              break;
            case "pcs":
              value = cellValue(row.pcs);
              break;
            case "grWt":
              value =
                row.ornament_weight !== undefined &&
                row.ornament_weight !== null &&
                row.ornament_weight !== ""
                  ? cellValue(row.ornament_weight)
                  : cellValue(row.gwt ?? row.GWT);
              break;
            case "lsWt":
              value = cellValue(row.lwt ?? row.LWT);
              break;
            case "netWt":
              value =
                row.payable_weight !== undefined &&
                row.payable_weight !== null &&
                row.payable_weight !== ""
                  ? cellValue(row.payable_weight)
                  : cellValue(row.nwt ?? row.NWT);
              break;
            case "itRateOn":
              value = cellValue(row.itrateon || row.item_rate_on || "NWt");
              break;
            case "itRate":
              value = cellValue(row.itrate);
              break;
            case "itAmt":
              value = cellValue(row.itamt);
              break;
            case "lType":
              value = cellValue(row.lbr_type || "NWt");
              break;
            case "lbrPercentage":
              value = cellValue(row.lbr_percent);
              break;
            case "lbrRate":
              value = cellValue(row.lbr_rate);
              break;
            case "lbrAmt":
              value = cellValue(row.lbr_amt);
              break;
            case "othAmt":
              value = cellValue(row.oth_amt ?? row.othamt);
              extra.otherAmountEntries = mapApiChargesToOtherAmountEntries(
                row.charges,
              );
              break;
            case "mrp":
              value = cellValue(row.mrp);
              break;
            case "image":
              value = cellValue(row.image);
              break;
            case "totalAmt":
              value = cellValue(
                row.totalamt ?? row.total_amount ?? row.charged_amt,
              );
              break;
            case "tagNo":
              // Prefer tag_no from each custom order entry row; avoid using order/co number
              value = cellValue(row.tag_no || row.tagNo);
              break;
            case "huid":
              value = cellValue(row.huid || row.HUID);
              break;
            default:
              value = "";
          }
          if (col.id === "image") {
            return { id: col.id, value, imageFile: null, imagePreview: null };
          }
          if (col.id === "othAmt") {
            return { id: col.id, value, ...extra };
          }
          return { id: col.id, value };
        }),
      }));
      setTableData(mappedTableData);

      if (locationState && locationState.customOrderData) {
        const order = locationState.customOrderData;
        if (order.round_off !== undefined) setRoundOffAmount(order.round_off);
        if (order.note !== undefined) setNote(order.note);
        // Autofill Exp Delivery if setter is provided and not already set
        if (
          setExpDate &&
          (order.exp_delivery || order.expected_delivery) &&
          !expDate
        ) {
          setExpDate(order.exp_delivery || order.expected_delivery);
        }
      }

      if (
        locationState &&
        locationState.customOrderPayments &&
        Array.isArray(locationState.customOrderPayments) &&
        locationState.customOrderPayments.length > 0
      ) {
        const mappedPayments = locationState.customOrderPayments.map((p, idx) =>
          mapApiPaymentToUiRow(p, idx),
        );
        setPaymentRows(mappedPayments);
      } else {
        // Ensure payment rows are always available even if no custom order payments
        setPaymentRows([
          {
            id: 1,
            pay_mode: "cash",
            type: "",
            remarks: "",
            amount: "0.00",
          },
          {
            id: 2,
            pay_mode: "cheque",
            type: "",
            remarks: "",
            amount: "0.00",
          },
          {
            id: 3,
            pay_mode: "bank_transfer",
            type: "",
            remarks: "",
            amount: "0.00",
          },
        ]);
      }

      setHasFilledFromCustomOrder(true);
    }
  }, [
    isEditMode,
    locationState,
    setTableData,
    setPaymentRows,
    setRoundOffAmount,
    setNote,
    COLUMNS,
    dropdownData,
    hasFilledFromCustomOrder,
    setHasFilledFromCustomOrder,
    setExpDate,
    expDate,
  ]);

  // Autofill customer details from custom order if present
  useEffect(() => {
    if (
      !isEditMode && // Don't run in edit mode
      locationState &&
      locationState.customOrderData
    ) {
      const order = locationState.customOrderData;
      const userId =
        order.customer_id ||
        order.customer_user_id ||
        order.user_id ||
        order.by_user;
      const foundUser =
        userId && Array.isArray(userss)
          ? userss.find((u) => String(u.id) === String(userId))
          : null;

      const name =
        foundUser?.fullName ||
        foundUser?.name ||
        order.customer_name ||
        order.user_name ||
        order.name ||
        "";
      const mobile =
        foundUser?.mobile ||
        order.customer_mobile ||
        order.user_mobile ||
        order.mobile ||
        "";

      if (!name && !mobile && !foundUser) return;

      if (name) {
        setCustomerName && setCustomerName(name);
        setCustomerNameSearchText && setCustomerNameSearchText(name);
      }
      if (mobile) {
        setCustomerMobile && setCustomerMobile(mobile);
        setCustomerMobileSearchText && setCustomerMobileSearchText(mobile);
      }
      if (foundUser?.id != null) {
        setSelectedCustomerId && setSelectedCustomerId(foundUser.id);
      } else if (userId) {
        setSelectedCustomerId && setSelectedCustomerId(userId);
      }

      if (foundUser) {
        setCustomerDetails &&
          setCustomerDetails({
            email: foundUser.email || "",
            birthDate: foundUser.birthDate || "",
            anniversaryDate: foundUser.anniversaryDate || "",
            companyName: foundUser.companyName || "",
            companyAddress: foundUser.companyAddress || "",
            gstNumber: foundUser.gstNumber || "",
            aadharNo: foundUser.aadharNo || "",
            panNo: foundUser.panNo || "",
            maritalStatus: foundUser.maritalStatus || "",
            balance: foundUser.balance || "",
            saleBalance: foundUser.saleBalance || "",
            reference: foundUser.reference || "",
            state: foundUser.state || "",
            area: foundUser.area || "",
          });
        setCustomerCity && setCustomerCity(foundUser.city || "");
        setCustomerAddress && setCustomerAddress(foundUser.address || "");
        setCustomerArea && setCustomerArea(foundUser.area || "");
        if (setSelectedState && foundUser.state && Array.isArray(states)) {
          const matchedState = states.find(
            (s) => s.name.toLowerCase() === foundUser.state.toLowerCase(),
          );
          setSelectedState(matchedState ? matchedState.iso2 : "");
        } else if (setSelectedState) {
          setSelectedState("");
        }
        if (setCustomerCity && foundUser.city && Array.isArray(cities)) {
          const matchedCity = cities.find(
            (c) => c.name.toLowerCase() === foundUser.city.toLowerCase(),
          );
          setCustomerCity(matchedCity ? matchedCity.name : "");
        }
      } else {
        setCustomerAddress &&
          setCustomerAddress(order.customer_address || order.address || "");
        setCustomerArea && setCustomerArea(order.area || "");
      }
    }
  }, [
    isEditMode,
    locationState,
    userss,
    setCustomerName,
    setCustomerNameSearchText,
    setCustomerMobile,
    setCustomerMobileSearchText,
    setSelectedCustomerId,
    setCustomerDetails,
    setCustomerCity,
    setCustomerAddress,
    setCustomerArea,
    setSelectedState,
    states,
    cities,
  ]);
}
